import type { HttpClient } from "@/lib/http";
import { http } from "@/lib/http";

import { compressImage } from "@/features/chat/lib/compress-image";
import type {
  ChatAttachment,
  ChatConversation,
  ChatMessage,
  ChatMessagesPage,
  ConversationsResult,
  SendMessageInput,
  StartConversationTarget,
} from "@/features/chat/types";

import type {
  ConversationsDto,
  MessageDto,
  MessagesDto,
  MessagesPageParams,
  StartConversationDto,
  UnreadDto,
  UploadDto,
} from "./dto";
import {
  mapConversation,
  mapConversations,
  mapMessageEnvelope,
  mapMessagesPage,
  mapUnread,
  mapUpload,
} from "./mappers";

/**
 * Transport for direct messaging (`/api/v1/chat/*`).
 *
 * v5 calls the backend directly (ADR-0003) — the legacy frontend's eleven
 * `/api/chat/*` Next.js proxy routes are deliberately not ported; `lib/http.ts`
 * already attaches the bearer token and request id.
 *
 * Real-time delivery rides Socket.IO (see `hooks/use-chat-socket.ts`); every
 * mutation here still returns the server-authoritative message so the sender
 * reconciles even when the socket is down.
 */
export interface ChatRepository {
  listConversations(page?: number): Promise<ConversationsResult>;
  startConversation(target: StartConversationTarget): Promise<ChatConversation>;
  clearConversation(conversationId: string): Promise<void>;
  getMessages(params: {
    conversationId: string;
    /** Cursor: `createdAt` of the oldest loaded message. Pages backwards. */
    before?: string;
    limit?: number;
  }): Promise<ChatMessagesPage>;
  sendMessage(input: SendMessageInput): Promise<ChatMessage>;
  editMessage(messageId: string, text: string): Promise<ChatMessage>;
  deleteMessage(messageId: string): Promise<ChatMessage>;
  reactToMessage(messageId: string, emoji: string): Promise<ChatMessage>;
  markRead(conversationId: string): Promise<void>;
  getUnreadCount(): Promise<number>;
  uploadAttachments(
    files: File[],
    options: { compressed: boolean },
  ): Promise<ChatAttachment[]>;
}

export function createChatRepository(client: HttpClient): ChatRepository {
  return {
    async listConversations(page = 1) {
      const dto = await client.post<ConversationsDto>(
        "/api/v1/chat/conversations",
        { page, limit: 30 },
      );
      return mapConversations(dto);
    },

    async startConversation(target) {
      const dto = await client.post<StartConversationDto>(
        "/api/v1/chat/conversations/start",
        { user_id: target.userId, username: target.username },
      );
      return mapConversation(dto.conversation ?? {});
    },

    async clearConversation(conversationId) {
      await client.post("/api/v1/chat/conversations/clear", {
        conversation_id: conversationId,
      });
    },

    async getMessages({ conversationId, before, limit = 30 }) {
      const body: MessagesPageParams = {
        conversation_id: conversationId,
        limit,
      };
      // Omit rather than send `before: undefined` — the backend treats a present
      // key as a cursor and would return an empty first page.
      if (before) body.before = before;
      const dto = await client.post<MessagesDto>("/api/v1/chat/messages", body);
      return mapMessagesPage(dto);
    },

    async sendMessage(input) {
      const dto = await client.post<MessageDto>("/api/v1/chat/messages/send", {
        conversation_id: input.conversationId,
        recipient_id: input.recipientId,
        username: input.username,
        text: input.text,
        attachments: input.attachments,
        reply_to: input.replyTo ?? null,
        client_id: input.clientId,
      });
      return mapMessageEnvelope(dto);
    },

    async editMessage(messageId, text) {
      const dto = await client.put<MessageDto>("/api/v1/chat/messages/edit", {
        message_id: messageId,
        text,
      });
      return mapMessageEnvelope(dto);
    },

    async deleteMessage(messageId) {
      const dto = await client.post<MessageDto>(
        "/api/v1/chat/messages/delete",
        {
          message_id: messageId,
        },
      );
      return mapMessageEnvelope(dto);
    },

    async reactToMessage(messageId, emoji) {
      const dto = await client.post<MessageDto>("/api/v1/chat/messages/react", {
        message_id: messageId,
        emoji,
      });
      return mapMessageEnvelope(dto);
    },

    async markRead(conversationId) {
      await client.post("/api/v1/chat/messages/read", {
        conversation_id: conversationId,
      });
    },

    async getUnreadCount() {
      const dto = await client.post<UnreadDto>("/api/v1/chat/unread", {});
      return mapUnread(dto);
    },

    async uploadAttachments(files, options) {
      // Multipart: `http.ts` skips its JSON Content-Type for FormData so the
      // browser sets the boundary itself.
      const form = new FormData();

      // Compress (when the composer's toggle is on) BEFORE the multipart body is
      // built — the point of the toggle is to send fewer bytes, so it has to run
      // on this side of the request. `compressImage` never throws and returns the
      // original file on any failure path, so a compression problem can't cost
      // the user their attachment.
      //
      // The per-file dimensions come back even when nothing was re-encoded, and
      // are merged into the response below: the backend doesn't always echo
      // width/height, and without them the thread reserves no space and shifts as
      // each image decodes.
      const localDimensions: Array<{
        width?: number;
        height?: number;
        compressed: boolean;
      }> = [];

      for (const file of files) {
        const result = await compressImage(file, options.compressed);
        form.append("files", result.file, result.file.name);
        localDimensions.push({
          width: result.width,
          height: result.height,
          compressed: result.compressed,
        });
      }

      form.append("compressed", String(options.compressed));

      const dto = await client.post<UploadDto>("/api/v1/chat/upload", form);
      const attachments = mapUpload(dto);

      // Server values win where present; the local read only fills the gaps.
      return attachments.map((attachment, index) => {
        const local = localDimensions[index];
        if (!local) return attachment;
        return {
          ...attachment,
          width: attachment.width ?? local.width,
          height: attachment.height ?? local.height,
          compressed: attachment.compressed || local.compressed,
        };
      });
    },
  };
}

/** Default repository wired to the app's singleton HTTP client. */
export const chatRepository = createChatRepository(http);
